import { useEffect, useState } from 'react';
import { ArrowBackOutlined } from '@mui/icons-material';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import {
  ChoiceControl, CompactEditButton, DefinitionList, FeedbackSnackbar, FormTextField,
  InformationPage, InlineNotice, PageSections, ResourceState, SectionCard, useFeedbackSnackbar,
} from '../../../design-system';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { absenceSyncSettingsError, emptyAbsenceSyncSettings } from '../model/absenceSyncSettings';
import type { AbsenceSyncSettings } from '../model/types';
import type { IntegrationSettingsPort } from '../port/IntegrationSettingsPort';

export function AbsenceSyncSettingsDetail({ port, fixtureMode, canWrite, readOnly, onBack }: {
  port: IntegrationSettingsPort; fixtureMode: boolean; canWrite: boolean; readOnly: boolean; onBack: () => void;
}) {
  const resource = useAsyncResource(() => fixtureMode ? Promise.resolve(emptyAbsenceSyncSettings) : port.getAbsenceSyncSettings(), [port, fixtureMode]);
  const [saved, setSaved] = useState<AbsenceSyncSettings>();
  const [draft, setDraft] = useState<AbsenceSyncSettings>();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [conflict, setConflict] = useState(false);
  const { notify, feedbackProps } = useFeedbackSnackbar();
  useEffect(() => {
    if (resource.data) { setSaved(resource.data); setDraft(resource.data); setEditing(false); setError(undefined); setConflict(false); }
  }, [resource.data]);
  const editable = canWrite && !readOnly;
  const validation = draft ? absenceSyncSettingsError(draft, saved) : undefined;
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const change = (next: Partial<AbsenceSyncSettings>) => { setDraft((value) => value ? { ...value, ...next } : value); setError(undefined); };
  const save = async () => {
    if (!editable || !editing || !draft || !dirty || validation || saving || conflict) return;
    setSaving(true); setError(undefined);
    try {
      const updated = fixtureMode ? { ...draft, version: draft.version + 1 } : await port.updateAbsenceSyncSettings(draft);
      setSaved(updated); setDraft(updated); setEditing(false);
      notify('휴가 동기화 설정을 저장했습니다.', { tone: 'success' });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : '휴가 동기화 설정을 저장하지 못했습니다.');
      setConflict(typeof failure === 'object' && failure !== null && 'code' in failure && failure.code === 'VERSION_CONFLICT');
    } finally { setSaving(false); }
  };
  return <InformationPage title="휴가·부재 동기화" help="설정한 대상에 확정 휴가와 정정·취소를 반영합니다. 실제 동기화에는 관리자의 Google Calendar 권한과 Slack 인증 설정이 필요하며, 사용 설정은 연결 성공 여부를 뜻하지 않습니다." width="standard" testId="absence-sync-settings"
    navigation={<Tooltip title="연동 목록으로 돌아가기"><IconButton aria-label="연동 목록으로 돌아가기" onClick={onBack} disabled={saving}><ArrowBackOutlined /></IconButton></Tooltip>}
  >
    <ResourceState loading={resource.loading} empty={!resource.loading && !saved} error={resource.error?.message} onRetry={resource.reload} loadingLabel="휴가 동기화 설정을 불러오는 중입니다.">
      {saved && draft && <PageSections>
        {readOnly && <InlineNotice severity="info">다른 사용자로 보는 동안에는 연동 설정을 변경할 수 없습니다.</InlineNotice>}
        <SectionCard title="동기화 대상과 근무 시간" help="Slack은 휴가 시작 시 휴가·반차·반반차 상태를 설정하고 종료 시 만료합니다. 사용자가 직접 바꾼 상태는 보존합니다." bodyPadding="standard" action={editable ? editing ? <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Button variant="outlined" color="inherit" disabled={saving} onClick={() => { setDraft(saved); setEditing(false); setError(undefined); setConflict(false); }}>취소</Button>
          <Button variant="outlined" disabled={saving || !dirty || Boolean(validation) || conflict} onClick={() => { void save(); }}>{saving ? '저장 중' : '저장'}</Button>
        </Stack> : <CompactEditButton aria-label="휴가 동기화 설정 수정" onClick={() => setEditing(true)}>수정</CompactEditButton> : undefined}>
          <Stack spacing={2}>
            {error && <InlineNotice severity="error" announcement="polite" action={conflict ? <Button onClick={resource.reload}>최신 설정 불러오기</Button> : undefined}>{error}{conflict ? ' 최신 설정을 불러오면 현재 편집 내용이 초기화됩니다.' : ''}</InlineNotice>}
            {editing ? <>
              <ChoiceControl kind="switch" label="Google Calendar 일정 동기화" checked={draft.calendarEnabled} disabled={saving} onCheckedChange={(calendarEnabled) => change({ calendarEnabled })} />
              <ChoiceControl kind="switch" label="Slack 상태 자동 변경" checked={draft.slackStatusEnabled} disabled={saving} onCheckedChange={(slackStatusEnabled) => change({ slackStatusEnabled })} />
              <FormTextField label="시간대" value={draft.timeZone} disabled={saving} required onChange={(event) => change({ timeZone: event.target.value })} helperText="예: Asia/Seoul" />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormTextField fullWidth type="time" label="근무 시작" value={draft.workdayStart} disabled={saving} onChange={(event) => change({ workdayStart: event.target.value })} required />
                <FormTextField fullWidth type="time" label="근무 종료" value={draft.workdayEnd} disabled={saving} onChange={(event) => change({ workdayEnd: event.target.value })} required />
              </Stack>
              {validation && <InlineNotice severity="info">{validation}</InlineNotice>}
            </> : <DefinitionList items={[
              ['Google Calendar 일정 동기화', saved.calendarEnabled ? '사용 설정됨' : '사용 안 함'],
              ['Slack 상태 자동 변경', saved.slackStatusEnabled ? '사용 설정됨' : '사용 안 함'],
              ['시간대', saved.timeZone], ['근무 시작', saved.workdayStart || '미설정'], ['근무 종료', saved.workdayEnd || '미설정'],
            ]} />}
            <DefinitionList items={[
              ['반차 경계', '오후 2시 30분'],
              ['오전 반차', '근무 시작부터 오후 2시 30분까지 · 0.5일'],
              ['오후 반차', '오후 2시 30분부터 근무 종료까지 · 0.5일'],
              ['오전 반반차', saved.workdayStart >= '11:30' ? '자동 반영하려면 근무 시작을 11:30 이전으로 설정' : '근무 시작부터 11:30까지 · 0.25일'],
              ['오후 반반차', saved.workdayEnd <= '16:30' ? '자동 반영하려면 근무 종료를 16:30 이후로 설정' : '16:30부터 근무 종료까지 · 0.25일'],
            ]} />
          </Stack>
        </SectionCard>
      </PageSections>}
    </ResourceState>
    <FeedbackSnackbar {...feedbackProps} />
  </InformationPage>;
}
